/* eslint-disable @typescript-eslint/promise-function-async */
import React, { useEffect } from 'react';
import { ToastList } from './ToastList';
import { GlobalThemeProvider, getDefaultZoomUI } from '../../ThemeProvider';
import { randomStr } from '../../Shared/random';
import { renderToContainer } from '../../Shared/renderToContainer';
export function getDefaultConfig() {
    const zoomUI = getDefaultZoomUI();
    return {
        placement: 'top',
        duration: 3000,
        zIndex: zoomUI.zIndex.Toast,
        maxCount: 8,
        screenReadOnly: false,
    };
}
export class ToastHub {
    constructor() {
        this.internalConfig = getDefaultConfig();
        this.container = null;
        this.mountStage = null;
        this.waitingQueue = [];
        this.statusMap = {};
        this.handlerRef = React.createRef();
    }
    createOpen(type, content, duration, onClose) {
        const config = this.internalConfig;
        let timeoutId = null;
        let data = {
            type,
            zIndex: config.zIndex,
            duration: config.duration,
            screenReadOnly: config.screenReadOnly,
            width: config.width,
            maxWidth: config.maxWidth,
        };
        if (typeof content === 'string') {
            data = { ...data, content, onClose };
            if (duration !== undefined && duration >= 0) {
                data.duration = duration;
            }
        }
        else {
            data = {
                ...data,
                ...content,
            };
        }
        if (data?.actionable) {
            data.duration = 0;
        }
        const toastData = { ...{ key: randomStr() }, ...data };
        const exsitStatus = this.statusMap[toastData.key];
        if (exsitStatus?.timeoutId && exsitStatus.timeoutId >= 0) {
            window.clearTimeout(exsitStatus.timeoutId);
        }
        const renderToastList = () => {
            const list = [];
            if (this.waitingQueue.length > 0) {
                while (this.waitingQueue.length > 0) {
                    const queueData = this.waitingQueue.shift();
                    if (queueData) {
                        list.push(queueData);
                    }
                }
            }
            this.handlerRef.current?.appendToasts(list);
        };
        if (this.mountStage === null) {
            this.mountStage = 'mounting';
            this.mount()
                .then(() => {
                renderToastList();
                this.mountStage = 'mounted';
            })
                .catch(
            /* istanbul ignore next */ (err) => {
                console.error(err);
            });
        }
        this.waitingQueue.push(toastData);
        if (this.mountStage === 'mounted') {
            renderToastList();
        }
        const delayToast = () => {
            if (toastData.duration !== undefined && toastData.duration > 0) {
                timeoutId = window.setTimeout(() => {
                    this?.handlerRef?.current?.destroyToast?.(toastData.key);
                    timeoutId = null;
                }, toastData.duration);
                this.statusMap[toastData.key] = {
                    duration,
                    timeoutId,
                };
            }
        };
        delayToast();
        const toastInstanceDestroy = () => {
            this.destroy(toastData.key);
        };
        const toastInstanceClose = () => {
            this.close(toastData.key);
        };
        const toastInstanceUpdate = (opts) => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            const waitingQueueIndex = this.waitingQueue.findIndex((item) => toastData.key === item.key);
            if (waitingQueueIndex >= 0) {
                const waitingItem = this.waitingQueue[waitingQueueIndex];
                this.waitingQueue[waitingQueueIndex] = {
                    ...waitingItem,
                    ...opts,
                    ...{
                        key: toastData.key,
                    },
                };
            }
            this?.handlerRef?.current?.updateToast?.(toastData.key, opts);
            delayToast();
        };
        return {
            destroy: toastInstanceDestroy,
            close: toastInstanceClose,
            update: toastInstanceUpdate,
        };
    }
    mount() {
        if (this.internalConfig.container) {
            this.container = this.internalConfig.container;
        }
        else {
            if (!this.container) {
                this.container = document.createElement('div');
            }
            document.body.appendChild(this.container);
        }
        const { internalConfig, handlerRef } = this;
        return new Promise((resolve) => {
            const App = () => {
                useEffect(() => {
                    resolve();
                }, []);
                return (React.createElement(GlobalThemeProvider, null,
                    React.createElement(ToastList, { hasContainer: !!internalConfig.container, handlerRef: handlerRef, defaultConfig: internalConfig })));
            };
            renderToContainer(React.createElement(App, null), this.container);
        });
    }
    info(content, duration, onClose) {
        return this.createOpen('info', content, duration, onClose);
    }
    success(content, duration, onClose) {
        return this.createOpen('success', content, duration, onClose);
    }
    error(content, duration, onClose) {
        return this.createOpen('error', content, duration, onClose);
    }
    warn(content, duration, onClose) {
        return this.createOpen('warn', content, duration, onClose);
    }
    warning(content, duration, onClose) {
        return this.createOpen('warning', content, duration, onClose);
    }
    destroy(key) {
        this.handlerRef?.current?.destroyToast?.(key);
    }
    close(key) {
        this.handlerRef?.current?.closeToast?.(key);
    }
    config(globalConfig) {
        this.internalConfig = {
            ...this.internalConfig,
            ...globalConfig,
        };
        if (this.mountStage === 'mounted') {
            this.handlerRef.current?.updateConfig({ ...this.internalConfig });
        }
    }
}
